import { ConfettiPiece, ConfettiShape } from './types'

const CONFETTI_COLORS = [
  '#8054FF',
  '#B8A0FF',
  '#FF8B73',
  '#7FD8B5',
  '#FFCB6B',
  '#FF6B9D',
]

const SHAPES: ConfettiShape[] = ['square', 'rectangle', 'circle', 'streamer']

/**
 * Builds a deterministic-ish set of confetti pieces using a simple
 * pseudo-random generator seeded from `trigger`. Pieces fan upward and
 * out from a central origin, then fall under gravity-shaped curves.
 *
 * @param {number} trigger - the seed (re-runs trigger fresh particles)
 * @param {number} pieceCount - how many pieces to render
 * @param {number} spreadDegrees - the cone half-angle in degrees
 * @param {number} durationMs - the total animation duration
 * @returns {ConfettiPiece[]} the pieces ready to render
 */
const buildConfettiPieces = (
  trigger: number,
  pieceCount: number,
  spreadDegrees: number,
  durationMs: number,
): ConfettiPiece[] => {
  const pieces: ConfettiPiece[] = []
  const seed = trigger === 0 ? 1 : trigger

  for (let index = 0; index < pieceCount; index += 1) {
    const random1 = pseudoRandom(seed * 9301 + index * 49297)
    const random2 = pseudoRandom(seed * 4801 + index * 23117)
    const random3 = pseudoRandom(seed * 7919 + index * 12821)
    const random4 = pseudoRandom(seed * 6151 + index * 5717)
    const random5 = pseudoRandom(seed * 3209 + index * 991)

    const angleDegrees = -90 + (random1 * 2 - 1) * spreadDegrees
    const angleRadians = (angleDegrees * Math.PI) / 180
    const distance = 220 + random2 * 180
    const endX = Math.cos(angleRadians) * distance
    const endY = Math.sin(angleRadians) * distance + random3 * 200

    const colorIndex = Math.floor(random4 * CONFETTI_COLORS.length)
    const shapeIndex = Math.floor(random5 * SHAPES.length)
    const size = 6 + random4 * 6

    pieces.push({
      id: index,
      startX: 0,
      startY: 0,
      endX,
      endY,
      rotateStart: random1 * 360,
      rotateEnd: random1 * 360 + 360 + random2 * 720,
      delayMs: Math.floor(random5 * 120),
      durationMs: Math.floor(durationMs * (0.85 + random4 * 0.3)),
      color: CONFETTI_COLORS[colorIndex],
      size,
      shape: SHAPES[shapeIndex],
    })
  }

  return pieces
}

/**
 * Tiny deterministic pseudo-random generator. Same seed → same output,
 * which lets confetti be slightly different per trigger but consistent
 * for a given trigger. Good enough — we're not sampling a Gaussian.
 *
 * @param {number} seed - the seed value
 * @returns {number} a pseudo-random float in [0, 1)
 */
const pseudoRandom = (seed: number): number => {
  const sine = Math.sin(seed) * 10000
  return sine - Math.floor(sine)
}

export { buildConfettiPieces }
